import React, { useEffect, useMemo, useRef } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { trackEvent, trackException } from "../utils/appInsights";

function serializePbiDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail.slice(0, 4000);
  if (detail instanceof Error) return detail.message.slice(0, 4000);
  try {
    return JSON.stringify(detail).slice(0, 4000);
  } catch {
    return String(detail).slice(0, 4000);
  }
}

export default function Reports({
  accessToken,
  reportId,
  groupId,
  reportKey,
  settings,
  height = "calc(100vh - 65px - 72px)",
  filters = [],
}) {
  const canEmbed = Boolean(accessToken && reportId && groupId);
  const embedUrl = useMemo(() => {
    return `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${groupId}&rs:embed=true`;
  }, [reportId, groupId]);

  const lastNotReadyKey = useRef(null);

  useEffect(() => {
    if (canEmbed) {
      lastNotReadyKey.current = null;
      return;
    }
    const reason = !accessToken
      ? "no_token"
      : !reportId || !groupId
        ? "missing_report_ids"
        : "unknown";
    const dedupeKey = `${reportKey ?? ""}:${reason}`;
    if (lastNotReadyKey.current === dedupeKey) return;
    lastNotReadyKey.current = dedupeKey;
    trackEvent("PowerBiEmbedNotReady", {
      reportKey: reportKey ?? "",
      reason,
      hasToken: Boolean(accessToken),
      hasReportId: Boolean(reportId),
      hasGroupId: Boolean(groupId),
    });
  }, [canEmbed, accessToken, reportId, groupId, reportKey]);

  if (!canEmbed) {
    return (
      <div style={{ padding: 16 }}>
        <strong>Power BI not ready.</strong>
        <div>
          {accessToken ? "Missing reportId/groupId." : "Missing access token."}
        </div>
      </div>
    );
  }

  return (
    <div style={{ height, width: "100%" }}>
      <PowerBIEmbed
        embedConfig={{
          type: "report",
          id: reportId,
          embedUrl,
          accessToken,
          tokenType: models.TokenType.Aad,
          settings: {
            panes: {
              filters: { visible: true }, 
              pageNavigation: { visible: true },
            },
            background: models.BackgroundType.Transparent,
            ...settings,
          },

          filters,
        }}
        cssClassName="powerbi-container"

        eventHandlers={
          new Map([
            [
              "loaded",
              () => {
              },
            ],
            [
              "rendered",
              () => {
              },
            ],
            [
              "error",
              (event) => {
                console.error("Power BI error:", event?.detail);
                const detailText = serializePbiDetail(event?.detail);
                trackEvent("PowerBiEmbedError", {
                  reportKey: reportKey ?? "",
                  reportId: reportId ?? "",
                  groupId: groupId ?? "",
                  detail: detailText,
                });
                if (event?.detail instanceof Error) {
                  trackException(event.detail, {
                    context: "PowerBiEmbed",
                    reportKey: reportKey ?? "",
                  });
                }
              },
            ],
          ])
        }
      />
    </div>
  );
}
